/**
 * <collapsible-text> — clamps rich text to a collapsed height and reveals it with a
 * "Ver mais" / "Ver menos" toggle. The toggle is only shown when the content actually
 * exceeds the collapsed height; otherwise the full text is shown with no toggle.
 */
class CollapsibleText extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector('[data-collapsible-wrapper]');
    this.content = this.querySelector('[data-collapsible-content]');
    this.toggle = this.querySelector('[data-collapsible-toggle]');
    if (!this.wrapper || !this.content || !this.toggle) return;

    this.collapsedHeight = parseInt(this.dataset.collapsed, 10) || 160;
    this.measure = this.measure.bind(this);
    this.handleToggle = this.handleToggle.bind(this);

    requestAnimationFrame(this.measure);
    this.toggle.addEventListener('click', this.handleToggle);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.content);
    }
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.handleToggle);
    this.resizeObserver?.disconnect();
  }

  #setMaxHeight(height) {
    this.wrapper.style.setProperty('--collection-seo-max', `${height}px`);
  }

  #applyState(expanded) {
    this.classList.toggle('is-expanded', expanded);
    this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.#setMaxHeight(expanded ? this.content.scrollHeight : this.collapsedHeight);
  }

  measure() {
    const fits = this.content.scrollHeight <= this.collapsedHeight + 1;
    if (fits) {
      this.toggle.hidden = true;
      this.classList.add('is-expanded');
      this.#setMaxHeight(this.content.scrollHeight);
      return;
    }
    this.toggle.hidden = false;
    this.#applyState(this.toggle.getAttribute('aria-expanded') === 'true');
  }

  handleToggle() {
    this.#applyState(this.toggle.getAttribute('aria-expanded') !== 'true');
  }
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}
